import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { PromptMethod } from '../types'

/**
 * Manages the global list of prompting methods (Zero-Shot, Few-Shot, etc.).
 * Methods are categorical tags for prompts — they are intentionally NOT tied
 * to any theoretical framework. KOSMOS is theory-neutral.
 */
export function usePromptMethods() {
  const [methods, setMethods] = useState<PromptMethod[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('prompt_methods')
      .select('*')
      .is('experiment_id', null) // global scope for now
      .order('sort_order')
      .order('name')
    if (error) console.error('usePromptMethods load:', error.message)
    setMethods((data ?? []) as PromptMethod[])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const createMethod = useCallback(
    async (name: string, description: string): Promise<PromptMethod | null> => {
      const trimmedName = name.trim()
      if (!trimmedName) return null
      const { data, error } = await supabase
        .from('prompt_methods')
        .insert({
          name: trimmedName,
          description: description.trim() || null,
          is_default: false,
          sort_order: methods.length + 100,
        })
        .select()
        .single()
      if (error) {
        console.error('createMethod:', error.message)
        return null
      }
      await load()
      return data as PromptMethod
    },
    [methods.length, load],
  )

  const deleteMethod = useCallback(
    async (id: string) => {
      const { error } = await supabase.from('prompt_methods').delete().eq('id', id)
      if (error) console.error('deleteMethod:', error.message)
      await load()
    },
    [load],
  )

  const getMethod = useCallback(
    (id: string | null): PromptMethod | undefined => methods.find((m) => m.id === id),
    [methods],
  )

  const getMethodByName = useCallback(
    (name: string): PromptMethod | undefined => methods.find((m) => m.name === name),
    [methods],
  )

  return { methods, loading, load, createMethod, deleteMethod, getMethod, getMethodByName }
}
